import { useToast } from '@chakra-ui/react';
import { ProductAccessRowStateType } from 'features/common/components/ProductAccessOptions/ProductAccessOptions';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useUpdateOrgGroupRole } from 'features/user-group/user-group-details/api/useUpdateOrgGroup';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { GroupDataApp } from 'features/user-group/user-group-list/components/groupListTableHelpers';

export const useAddAppsToGroup = () => {
  const [productAccessState, setProductAccessState] =
    useState<ProductAccessRowStateType>([]);
  const [{ data: drawerData }, drawerDispatch] = useDrawerStateDispatch();
  const { mutate: updateGroup, isLoading: loading } = useUpdateOrgGroupRole();
  const toast = useToast();
  const { tenantList, groupId } = drawerData || {};

  const newTenants: GroupDataApp[] = productAccessState.map((item) => ({
    scope: item.product.productCode,
    role: item.accessType.value,
    tenantId: item.product.id,
  }));

  const tenants = useMemo(() => {
    if (!tenantList) return newTenants;

    const existingTenantList: GroupDataApp[] = tenantList.map(
      (item: GroupDataApp) => ({
        scope: item.scope,
        role: item.role,
        tenantId: item.tenantId,
      }),
    );

    const newTenantIds = new Set(newTenants.map((role) => role.tenantId));
    const filteredExistingTenantList = existingTenantList.filter(
      (role: GroupDataApp) => !newTenantIds.has(role.tenantId),
    );
    return [...filteredExistingTenantList, ...newTenants];
  }, [tenantList, newTenants]);

  // Formik setup
  const formik = useFormik({
    initialValues: tenantList,
    onSubmit: async () => {
      try {
        updateGroup(
          {
            groupId: groupId,
            roles: tenants,
          },
          {
            onSuccess: () => {
              toast({
                title: 'Apps added successfully',
                description:
                  'The apps have been successfully added to the group.',
                status: 'success',
              });
              setProductAccessState([]);
              drawerDispatch(closeDrawer());
            },
            onError: (error) => {
              console.error('Error in form submission:', error);
              toast({
                title: 'Apps addition failed',
                description:
                  //@ts-expect-error
                  error?.response?.data?.detail ||
                  'An error occurred while adding apps. Please try again.',
                status: 'error',
              });
            },
          },
        );
      } catch (error) {
        console.error('Error in form submission:', error);
        toast({
          title: 'Apps addition failed',
          description:
            //@ts-expect-error
            error?.response?.data?.detail ||
            'An error occurred while adding apps. Please try again.',
          status: 'error',
        });
      }
    },
  });

  const handleCloseDrawer = () => {
    formik.resetForm();
    setProductAccessState([]);
    drawerDispatch(closeDrawer());
  };

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleSubmit: formik.handleSubmit,
    handleBlur: formik.handleBlur,
    isSubmitting: formik.isSubmitting || loading,
    resetForm: formik.resetForm,

    // Additional functionality
    tenantList,
    productAccessState,
    setProductAccessState,
    handleCloseDrawer,
  };
};
